'use client';

import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { Upload, Download, Copy, Trash2, ArrowRight, ArrowDown, Code, Settings } from 'lucide-react';
import { saveAs } from 'file-saver';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { sqlFormatterContent } from '../../data/sql-formatter-content';
import Link from 'next/link';

// Using sql-formatter library for SQL formatting
import { format } from 'sql-formatter';

interface CodeEditorProps {
    value: string;
    onChange?: (value: string) => void;
    readOnly?: boolean;
    placeholder?: string;
    className?: string;
}

const CodeEditor = ({ value, onChange, readOnly, placeholder, className }: CodeEditorProps) => {
    return (
        <div className={cn(
            "relative flex h-full w-full overflow-hidden rounded-md border bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
            className
        )}>
            <Textarea
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                readOnly={readOnly}
                className="h-full w-full resize-none border-0 focus-visible:ring-0 rounded-none p-4 font-mono text-sm leading-6"
                placeholder={placeholder}
                spellCheck={false}
            />
        </div>
    );
}

type SqlDialect = 'sql' | 'mariadb' | 'mysql' | 'postgresql' | 'db2' | 'plsql' | 'n1ql' | 'redshift' | 'spark' | 'tsql';

interface FormatOptions {
    language: SqlDialect;
    tabWidth: number;  // Changed from 'indent: string'
    useTabs: boolean;  // Added for tab support
    keywordCase: 'preserve' | 'upper' | 'lower';  // Changed from 'uppercase: boolean'
    linesBetweenQueries: number;
}


const defaultOptions: FormatOptions = {
    language: 'sql',
    tabWidth: 2,  // Changed from indent: '  '
    useTabs: false,
    keywordCase: 'upper',  // Changed from uppercase: true
    linesBetweenQueries: 2
};

const formatSql = (sql: string, options: FormatOptions): { formatted: string; error?: string } => {
    try {
        if (!sql.trim()) return { formatted: '', error: 'Empty SQL content' };

        const formatted = format(sql, {
            language: options.language,
            tabWidth: options.tabWidth,  // Updated to use tabWidth
            useTabs: options.useTabs,    // Added tab support
            keywordCase: options.keywordCase,  // Updated to use keywordCase
            linesBetweenQueries: options.linesBetweenQueries,
        });

        return { formatted };
    } catch (error) {
        console.error('Error formatting SQL:', error);
        return {
            formatted: sql,
            error: error instanceof Error ? error.message : 'An error occurred while formatting SQL'
        };
    }
};

export default function SqlFormatter() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [options, setOptions] = useState<FormatOptions>(defaultOptions);
    const [isOptionsOpen, setIsOptionsOpen] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { toast } = useToast();

    const formatSqlHandler = () => {
        try {
            if (!input.trim()) {
                toast({
                    title: "Error",
                    description: "Please enter some SQL to format",
                    variant: "destructive",
                });
                return;
            }

            const { formatted, error } = formatSql(input, options);

            if (error) {
                throw new Error(error);
            }

            setOutput(formatted);

            toast({
                title: "Success",
                description: "SQL formatted successfully!",
            });
        } catch (error) {
            toast({
                title: "Error",
                description: error instanceof Error ? error.message : "An error occurred while formatting SQL",
                variant: "destructive",
            });
            console.error('Error formatting SQL:', error);
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInput(content);
            toast({
                title: "File Uploaded",
                description: `Loaded ${file.name}`,
            });
        };
        reader.readAsText(file);
    };

    const handleDownload = () => {
        if (!output) return;
        const blob = new Blob([output], { type: "text/plain;charset=utf-8" });
        saveAs(blob, "formatted.sql");
        toast({
            title: "Downloaded",
            description: "File saved as formatted.sql",
        });
    };

    const copyToClipboard = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        toast({
            title: "Copied!",
            description: "Formatted SQL copied to clipboard",
        });
    };

    const clearAll = () => {
        setInput('');
        setOutput('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const sampleSql = `-- Sample SQL Query
SELECT 
    u.user_id,
    u.username,
    u.email,
    COUNT(o.order_id) AS total_orders,
    SUM(o.amount) AS total_spent
FROM 
    users u
LEFT JOIN 
    orders o ON u.user_id = o.user_id
WHERE 
    u.status = 'active'
    AND o.order_date >= '2023-01-01'
GROUP BY 
    u.user_id, u.username, u.email
HAVING 
    COUNT(o.order_id) > 5
ORDER BY 
    total_spent DESC
LIMIT 10;`;

    const loadSample = () => {
        setInput(sampleSql);
    };

    return (
        <div className="container mx-auto p-4 max-w-[1400px]">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <ToolPageIntro title="SQL Formatter">
                Format and beautify SQL code with our free online tool. Clean up messy SQL queries and improve code readability with proper indentation.
            </ToolPageIntro>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsOptionsOpen(!isOptionsOpen)}
                        className="gap-2"
                    >
                        <Settings className="h-4 w-4" />
                        {isOptionsOpen ? 'Hide Options' : 'Format Options'}
                    </Button>
                </div>
            </div>

            {/* Options Panel */}
            {/* Options Panel */}
            {isOptionsOpen && (
                <Card className="mb-6">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg">Formatting Options</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium leading-none">SQL Dialect</label>
                            <select
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                value={options.language}
                                onChange={(e) => setOptions({ ...options, language: e.target.value as SqlDialect })}
                            >
                                <option value="sql">Standard SQL</option>
                                <option value="mariadb">MariaDB</option>
                                <option value="mysql">MySQL</option>
                                <option value="postgresql">PostgreSQL</option>
                                <option value="db2">DB2</option>
                                <option value="plsql">PL/SQL</option>
                                <option value="n1ql">N1QL</option>
                                <option value="redshift">Redshift</option>
                                <option value="spark">Spark SQL</option>
                                <option value="tsql">T-SQL</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium leading-none">Indentation</label>
                            <select
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                value={options.useTabs ? 'tab' : options.tabWidth.toString()}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === 'tab') {
                                        setOptions({ ...options, useTabs: true, tabWidth: 4 });
                                    } else {
                                        setOptions({ ...options, useTabs: false, tabWidth: parseInt(value) || 2 });
                                    }
                                }}
                            >
                                <option value="2">2 spaces</option>
                                <option value="4">4 spaces</option>
                                <option value="tab">Tab</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium leading-none">Keywords</label>
                            <select
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                value={options.keywordCase}
                                onChange={(e) => setOptions({ ...options, keywordCase: e.target.value as 'preserve' | 'upper' | 'lower' })}
                            >
                                <option value="upper">UPPERCASE</option>
                                <option value="lower">lowercase</option>
                                <option value="preserve">As is</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium leading-none">Lines Between Queries</label>
                            <input
                                type="number"
                                min="0"
                                max="10"
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                value={options.linesBetweenQueries}
                                onChange={(e) => setOptions({ ...options, linesBetweenQueries: parseInt(e.target.value) || 0 })}
                            />
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-250px)]">
                {/* Input Section */}
                <Card className="flex-1 flex flex-col min-h-[500px] lg:h-full">
                    <CardHeader className="pb-3 border-b bg-muted/20">
                        <CardTitle className="flex justify-between items-center">
                            <span>Input SQL</span>
                            <div className="flex items-center gap-2">
                                <Button variant="ghost" size="sm" onClick={loadSample} className="text-muted-foreground">
                                    Sample
                                </Button>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileUpload}
                                    accept=".sql,.txt"
                                    className="hidden"
                                    id="file-upload"
                                />
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="text-muted-foreground"
                                >
                                    <Upload className="h-4 w-4 mr-1" /> Upload
                                </Button>
                                <Button variant="ghost" size="sm" onClick={clearAll} className="text-muted-foreground hover:text-destructive">
                                    <Trash2 className="h-4 w-4" /> Clear
                                </Button>
                            </div>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1 p-0 relative overflow-hidden">
                        <CodeEditor
                            value={input}
                            onChange={setInput}
                            placeholder="Paste your SQL here, upload a file, or try the sample..."
                            className="border-0 rounded-none"
                        />
                    </CardContent>
                </Card>

                {/* Action Section */}
                <div className="flex lg:flex-col justify-center items-center gap-4 py-4 lg:py-0">
                    <Button
                        onClick={formatSqlHandler}
                        size="lg"
                        className="rounded-full w-16 h-16 lg:w-20 lg:h-20 p-0 shadow-lg hover:scale-105 transition-transform"
                        title="Format SQL"
                    >
                        <ArrowRight className="h-8 w-8 hidden lg:block" />
                        <ArrowDown className="h-8 w-8 lg:hidden" />
                    </Button>
                </div>

                {/* Output Section */}
                <Card className="flex-1 flex flex-col min-h-[500px] lg:h-full">
                    <CardHeader className="pb-3 border-b bg-muted/20">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <CardTitle className="text-lg font-medium">Formatted Output</CardTitle>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="icon" onClick={copyToClipboard} disabled={!output} title="Copy">
                                    <Copy className="h-4 w-4" />
                                </Button>
                                <Button variant="outline" size="icon" onClick={handleDownload} disabled={!output} title="Download">
                                    <Download className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="flex-1 p-0 bg-muted/30 relative overflow-hidden">
                        <CodeEditor
                            value={output}
                            readOnly
                            placeholder="Formatted SQL will appear here..."
                            className="border-0 rounded-none bg-transparent"
                        />
                    </CardContent>
                </Card>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={sqlFormatterContent.title}
                description={sqlFormatterContent.description}
                content={sqlFormatterContent.content}
                faqs={sqlFormatterContent.faqs}
                relatedTools={sqlFormatterContent.relatedTools}
            />

            <Toaster />
        </div>
    );
}
